/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { IconButton, Typography2, VisuallyHidden, breakpoints } from '@coursera/cds-core';
import {
  CheckIcon,
  CopyIcon,
  ReloadIcon,
  ThumbsDownFilledIcon,
  ThumbsDownIcon,
  ThumbsUpFilledIcon,
  ThumbsUpIcon,
} from '@coursera/cds-icons';

import _t from 'i18n!nls/cds-labs';

const shimmer = keyframes`
  0% { background-position: 0% center; }
  100% { background-position: 80% center; }
`;

const styles = {
  userRoot: css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-bottom: var(--cds-spacing-200);
  `,
  userBubble: css`
    background: var(--cds-color-emphasis-primary-background-xweak);
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 var(--cds-border-radius-100);
    max-width: 100%;

    [dir='rtl'] & {
      border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) var(--cds-border-radius-100) 0;
    }
  `,
  userBubbleShort: css`
    display: inline-block;
  `,
  userBubbleLong: css`
    width: 100%;
  `,

  llmRoot: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
    padding: 0 0 var(--cds-spacing-200);

    &:hover [data-chat-role='past'],
    &:focus-within [data-chat-role='past'] {
      opacity: 1;
      pointer-events: auto;
    }
  `,
  richContent: css`
    color: var(--cds-color-neutral-primary);

    p:not([class]) {
      margin: 0;
    }

    p:not([class]) + p:not([class]) {
      margin-top: var(--cds-spacing-100);
    }

    strong {
      font-weight: 600;
    }
  `,

  loadingText: css`
    color: var(--cds-color-emphasis-neutral-content-default);
    background: linear-gradient(
      90deg,
      var(--cds-color-emphasis-neutral-content-default) 0%,
      var(--cds-color-emphasis-neutral-content-default) 33%,
      var(--cds-color-neutral-background-primary) 34%,
      var(--cds-color-emphasis-neutral-content-default) 44%,
      var(--cds-color-emphasis-neutral-content-default) 100%
    );
    background-size: 300% auto;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${shimmer} 2s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      background: none;
      -webkit-text-fill-color: unset;
      color: var(--cds-color-emphasis-neutral-content-default);
    }
  `,

  actionsRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    margin-left: calc(-1 * var(--cds-spacing-50));

    & button {
      color: var(--cds-color-interactive-secondary-inactive);
    }
  `,

  actionsPast: css`
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease-in-out;

    ${breakpoints.down('sm')} {
      opacity: 1;
      pointer-events: auto;
    }
  `,

  actionsLatest: css`
    opacity: 1;
  `,

  liveRegionCollapsed: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `,
};

const USER_BUBBLE_INLINE_CHAR_THRESHOLD = 60;

export type LlmActionButtonsConfig = {
  onCopy?: () => void;
  /**
   * Called on every thumbs-up press — both select and deselect. The consumer is
   * responsible for toggling its own state and reflecting it via `thumbsUpSelected`.
   * When omitted the thumbs-up button is not rendered.
   */
  onThumbsUp?: () => void;
  /** Whether the thumbs-up button should appear selected. Driven by the consumer's state. */
  thumbsUpSelected?: boolean;
  /**
   * Called on every thumbs-down press — both select and deselect.
   * When omitted the thumbs-down button is not rendered.
   */
  onThumbsDown?: () => void;
  /** Whether the thumbs-down button should appear selected. Driven by the consumer's state. */
  thumbsDownSelected?: boolean;
  /** Regenerate action — rendered when provided */
  onRegenerate?: () => void;
  /** Rendered directly in the actions row after the standard buttons */
  extraActions?: React.ReactNode;
  /**
   * When false the copy button is hidden. Useful when the consumer provides its
   * own copy action via `extraActions`. Defaults to true.
   */
  showCopy?: boolean;
};

type LlmChatMessageProps = {
  variant: 'llm';
  /**
   * Text or rich React content to display. When `loadingMessages` is set this is ignored.
   * Pass incrementally-updated strings to achieve a streaming effect.
   *
   * Note: when `content` is a non-string React node the bubble always renders full-width,
   * regardless of the node's visual length. Pass a plain string for inline (short) bubble sizing.
   */
  content?: React.ReactNode;
  /**
   * Activates the shimmer loading animation and cycles through the provided messages.
   * When provided, `content` is ignored. Pass an empty array to show no text during loading.
   */
  loadingMessages?: string[];
  /**
   * Whether this is the latest message in the thread.
   * Latest messages always show action buttons; past messages reveal them on hover/focus (desktop)
   * or always (mobile).
   */
  isLatestMessage?: boolean;
  /**
   * Force action buttons to always be visible regardless of hover/focus state.
   * Useful for mobile simulations and testing.
   */
  showActionsAlways?: boolean;
  /** Action button callbacks and optional slots */
  actionButtons?: LlmActionButtonsConfig;
  className?: string;
};

type UserChatMessageProps = {
  variant: 'user';
  content: React.ReactNode;
  className?: string;
};

export type ChatMessageProps = LlmChatMessageProps | UserChatMessageProps;

const LlmActionButtons = ({
  config,
  copied,
  onCopy,
  isLatestMessage,
  showActionsAlways,
}: {
  config: LlmActionButtonsConfig;
  copied: boolean;
  onCopy: () => void;
  isLatestMessage: boolean;
  showActionsAlways: boolean;
}) => {
  const visible = isLatestMessage || showActionsAlways;

  return (
    <div
      data-chat-role={visible ? undefined : 'past'}
      css={[styles.actionsRow, visible ? styles.actionsLatest : styles.actionsPast]}
      role="toolbar"
      aria-label={_t('Message actions')}
    >
      {config.showCopy !== false && (
        <IconButton
          icon={copied ? <CheckIcon color="success" size="small" /> : <CopyIcon size="small" />}
          size="small"
          variant="ghostSecondary"
          edgeAlign="noPadding"
          aria-label={copied ? _t('Copied') : _t('Copy message')}
          tooltip={copied ? _t('Copied!') : _t('Copy')}
          onClick={onCopy}
        />
      )}
      {config.onThumbsUp !== undefined && (
        <IconButton
          icon={
            config.thumbsUpSelected ? (
              <ThumbsUpFilledIcon color="default" size="small" />
            ) : (
              <ThumbsUpIcon size="small" />
            )
          }
          size="small"
          variant="ghostSecondary"
          edgeAlign="noPadding"
          aria-label={_t('Helpful')}
          aria-pressed={!!config.thumbsUpSelected}
          tooltip={_t('Helpful')}
          onClick={config.onThumbsUp}
        />
      )}
      {config.onThumbsDown !== undefined && (
        <IconButton
          icon={
            config.thumbsDownSelected ? (
              <ThumbsDownFilledIcon color="default" size="small" />
            ) : (
              <ThumbsDownIcon size="small" />
            )
          }
          size="small"
          variant="ghostSecondary"
          edgeAlign="noPadding"
          aria-label={_t('Not helpful')}
          aria-pressed={!!config.thumbsDownSelected}
          tooltip={_t('Not helpful')}
          onClick={config.onThumbsDown}
        />
      )}
      {config.onRegenerate && (
        <IconButton
          icon={<ReloadIcon size="small" />}
          size="small"
          variant="ghostSecondary"
          edgeAlign="noPadding"
          aria-label={_t('Regenerate response')}
          tooltip={_t('Regenerate')}
          onClick={config.onRegenerate}
        />
      )}
      {config.extraActions}
    </div>
  );
};

const UserChatMessage = ({ content, className }: UserChatMessageProps) => {
  const isLong = typeof content !== 'string' || content.length > USER_BUBBLE_INLINE_CHAR_THRESHOLD;

  return (
    <div css={styles.userRoot} className={className} data-testid="chat-message-user">
      <div css={[styles.userBubble, isLong ? styles.userBubbleLong : styles.userBubbleShort]}>
        <Typography2 variant="bodyPrimary" component="p">
          {content}
        </Typography2>
      </div>
    </div>
  );
};

const LlmChatMessage = ({
  content,
  loadingMessages,
  isLatestMessage = false,
  showActionsAlways = false,
  actionButtons,
  className,
}: Omit<LlmChatMessageProps, 'variant'>) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  useEffect(() => {
    setMsgIndex(0);
    if (!loadingMessages || loadingMessages.length <= 1) return;
    const id = setInterval(() => {
      setMsgIndex((i) => (i + 1) % loadingMessages.length);
    }, 2000);
    return () => clearInterval(id);
  }, [loadingMessages]);

  const handleCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    const text = contentRef.current?.textContent ?? '';
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        actionButtons?.onCopy?.();
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // clipboard write failed; no UI change needed
      });
  };

  const isLoading = loadingMessages !== undefined;

  return (
    <div css={styles.llmRoot} className={className} data-testid="chat-message-llm">
      {/* Always first in the DOM so screen readers register it before any content changes */}
      <VisuallyHidden role="status" aria-live="polite">
        {copied ? _t('Copied!') : ''}
      </VisuallyHidden>
      {/* Live region always in DOM so screen readers detect content updates */}
      <Typography2
        variant="bodyPrimary"
        component="p"
        css={isLoading ? styles.loadingText : styles.liveRegionCollapsed}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {isLoading ? (loadingMessages[msgIndex] ?? '') : ''}
      </Typography2>
      {!isLoading && (
        <div ref={contentRef} css={styles.richContent}>
          {typeof content === 'string' ? (
            <Typography2 variant="bodyPrimary" component="p">
              {content}
            </Typography2>
          ) : (
            content
          )}
        </div>
      )}
      {actionButtons && !isLoading && (
        <LlmActionButtons
          config={actionButtons}
          copied={copied}
          onCopy={handleCopy}
          isLatestMessage={isLatestMessage}
          showActionsAlways={showActionsAlways}
        />
      )}
    </div>
  );
};

export const ChatMessage = (props: ChatMessageProps) => {
  if (props.variant === 'user') {
    return <UserChatMessage {...props} />;
  }
  return <LlmChatMessage {...props} />;
};

export default ChatMessage;
